import { getChartDefaultOptions } from "./ChartJsConfig";
import { useTheme } from "@mui/material/styles";
import appDefaultTheme from "@/config/muiTheme";
import { getChartTextColor } from "./chartTextColor";
import React, { useState, useEffect, useMemo } from "react";
import * as ReactChartJs from "react-chartjs-2";

import { makeUniqChartData, RPNumberFormat } from "@/helpers/utils";
import { getDatasetDefaultOptions } from "./ChartJsConfig";

type ChartRow = Record<string, string | number | null | undefined>;

type DataFormat = {
  prefix: string;
  suffix: string;
};

type DataFormats = Record<string, DataFormat>;

type BarChartProps = {
  data?: ChartRow[];
  labels: string[];
  option?: Record<string, unknown>;
  scales?: Record<string, unknown>;
  title?: string;
  formats?: DataFormats | null;
};

type TooltipItem = {
  index: number;
};

type TooltipData = {
  datasets: Array<{
    label: string;
    data: Array<{ y?: string | number | null }>;
  }>;
};

const { Bar } = ReactChartJs as unknown as {
  Bar: React.ComponentType<Record<string, unknown>>;
};
const BarChart = ({
  data = [],
  labels,
  option = {},
  scales = {},
  title = "",
  formats = null,
}: BarChartProps) => {
  const providedTheme = useTheme();
  const theme = providedTheme.palette.neutral ? providedTheme : appDefaultTheme;
  const [chartData, setChartData] = useState<Record<string, unknown>>({});

  const options = useMemo(
    () => ({
      ...getChartDefaultOptions(theme),
      tooltips: {
        ...getChartDefaultOptions(theme).tooltips,
        //intersect: false,
        callbacks: {
          label: (tooltipItem: TooltipItem, data: TooltipData) => {
            const label = data.datasets[0].label;

            const val = data.datasets[0].data[tooltipItem.index] || {};

            const format = formats ? formats[labels[1]] : null;
            if (!val.y) return "";
            if (formats && format) {
              return `${label}: ${RPNumberFormat({
                prefix: format.prefix,
                suffix: format.suffix,
                thousand: ",",
              }).to(parseFloat(`${val.y}`))}`;
            }
            return `${label}: ${RPNumberFormat().to(parseFloat(`${val.y}`))}`;
          },
          title: (tooltipItem: Array<{ label: string }>) => {
            const label = tooltipItem[0].label;
            return label;
          },
        },
      },
      legend: {
        display: false,
        align: "start",
        labels: {
          boxWidth: 10,
          fontColor: getChartTextColor(theme),
          padding: 5,
          marginBottom: 10,
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
            },
            ticks: {
              fontColor: getChartTextColor(theme),
              callback: (value: string) => {
                return value.slice(0, 7) + `${value.length > 7 ? ".." : ""}`;
              },
            },
          },
        ],
        yAxes: [
          {
            id: "y-axis-0",
            position: "left",
            ticks: {
              fontColor: getChartTextColor(theme),
              min: 0,
              maxTicksLimit: 5,
              callback: (value: string | number) => {
                const format = formats ? formats[labels[1]] : null;
                if (!value) return "";
                if (formats && format) {
                  return RPNumberFormat({
                    prefix: format.prefix,
                    suffix: format.suffix,
                    thousand: ",",
                  }).to(value);
                }
                return RPNumberFormat().to(value);
              },
            },
          },
        ],
        ...scales,
      },
      ...option,
    }),
    [option, scales, theme]
  );

  const getDataSet = () => {
    let formatData: Array<{ x: ChartRow[string]; y: ChartRow[string] }> = [];

    formatData = makeUniqChartData(data, labels[0]).map((d) => ({
      x: d[labels[0]],
      y: d[labels[1]],
    }));

    const datasets = [
      {
        label: title,
        data: formatData,
        yAxisID: "y-axis-0",
        ...getDatasetDefaultOptions(theme),
      },
    ];

    return datasets;
  };

  useEffect(() => {
    const initData = {
      labels: getDataSet()[0].data ? getDataSet()[0].data.map((d) => d.x) : [],
      datasets: getDataSet(),
    };
    setChartData(initData);
  }, [data, theme]);

  return (
    <div className="area-chart">
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default BarChart;
